import { useEffect, useState } from "react";
import { Smartphone, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackFunnel } from "@/lib/funnel";

const DISMISS_KEY = "alta_pwa_dismissed_at";
const LOGIN_COUNT_KEY = "alta_login_count";
const DISMISS_DAYS = 30;
const MIN_LOGINS_TO_SHOW = 2;

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 820px)").matches;
}

function isStandaloneAlready() {
  if (typeof window === "undefined") return false;
  // PWA already installed and opened from home screen
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isDismissed() {
  try {
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (!ts) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch (_) {
    return false;
  }
}

function getLoginCount() {
  try {
    return parseInt(localStorage.getItem(LOGIN_COUNT_KEY) || "0", 10);
  } catch (_) {
    return 0;
  }
}

export default function PWAInstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const shouldShow = () =>
      !isStandaloneAlready() &&
      isMobileViewport() &&
      !isDismissed() &&
      getLoginCount() >= MIN_LOGINS_TO_SHOW;

    const onBefore = (e) => {
      e.preventDefault(); // suppress browser's automatic prompt
      // Re-check conditions at event-time so localStorage updates (login count) are honoured
      if (!shouldShow()) return;
      setDeferredPrompt(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  const handleAdd = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice && choice.outcome === "accepted") {
        try { localStorage.removeItem(DISMISS_KEY); } catch (_) { /* ignore */ }
        trackFunnel("pwa_install_accepted", { meta: { source: "custom_banner" } });
      } else {
        // declined — same as "not now"
        try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
        trackFunnel("pwa_install_dismissed", { meta: { source: "custom_banner", reason: "native_declined" } });
      }
    } catch (_) {
      // swallow — prompt() can throw if called twice
    }
    setDeferredPrompt(null);
    setVisible(false);
  };

  const handleDismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) { /* ignore */ }
    trackFunnel("pwa_install_dismissed", { meta: { source: "custom_banner", reason: "user_dismissed" } });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-40 max-w-md mx-auto rounded-xl border border-[#E8E6E1] bg-white shadow-lg p-4 animate-in slide-in-from-bottom-4"
      role="dialog"
      aria-labelledby="pwa-install-title"
      data-testid="pwa-install-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-[#DC2626] flex items-center justify-center flex-shrink-0">
          <Smartphone className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div id="pwa-install-title" className="font-display font-semibold text-[#0A0A0A] text-sm leading-tight">
            Adicionar Alta à tela inicial?
          </div>
          <div className="text-xs text-[#6B6B6B] mt-1 leading-snug">
            Acesso rápido pelo celular, funciona até offline. Sem usar a loja de apps.
          </div>
          <div className="flex gap-2 mt-3">
            <Button size="sm" className="bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs h-8" onClick={handleAdd} data-testid="pwa-install-accept">
              Adicionar
            </Button>
            <Button size="sm" variant="outline" className="border-[#E8E6E1] text-xs h-8" onClick={handleDismiss} data-testid="pwa-install-dismiss">
              Agora não
            </Button>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          aria-label="Fechar"
          className="text-[#9B9B9B] hover:text-[#0A0A0A] p-1 -mt-1 -mr-1"
          data-testid="pwa-install-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
